import { Alert, Button, EmptyState, LoadingState, Textarea } from '@orbind/ui'
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router'
import { MarkdownDoAssistente } from '../features/ai-assistant/markdown.tsx'
import {
  ROTULO_DA_FERRAMENTA,
  useEnviarAoAssistente,
  useSituacaoDoAssistente,
  type MensagemDoHistorico,
} from '../features/ai-assistant/useAssistente.ts'
import './assistente.css'

/**
 * Assistente com IA (SPEC-20261104-2229) — conversa com o Claude DENTRO do
 * Orbind, com as ferramentas, permissões e confirmações do conector.
 *
 * Estados: carregando; erro; fora do plano; sem liberação do administrador;
 * servidor sem chave; conversa vazia (sugestões); conversa; pensando; erro de
 * uma resposta (a mensagem volta para a caixa, nada se perde).
 */

type Fala = { quem: 'pessoa' | 'assistente'; texto: string; acoes?: { ferramenta: string; erro: boolean }[] }

/** O Textarea do @orbind/ui não repassa ref: a caixa é achada pelo id. */
const ID_DA_CAIXA = 'assistente-mensagem'
const focarCaixa = () => document.getElementById(ID_DA_CAIXA)?.focus()

const SUGESTOES = [
  'O que eu tenho para hoje?',
  'Quais Itens meus estão atrasados?',
  'Resuma o andamento dos Itens que acompanho.',
  'Crie um Item a partir desta ideia: …',
]

export function AssistentePage() {
  const navigate = useNavigate()
  const situacao = useSituacaoDoAssistente()
  const enviar = useEnviarAoAssistente()
  const [falas, setFalas] = useState<Fala[]>([])
  const [historico, setHistorico] = useState<MensagemDoHistorico[]>([])
  const [rascunho, setRascunho] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const fimRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fimRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' })
  }, [falas.length, enviar.isPending])

  const mandar = (texto: string) => {
    const mensagem = texto.trim()
    if (!mensagem || enviar.isPending) return
    setErro(null)
    setRascunho('')
    setFalas((f) => [...f, { quem: 'pessoa', texto: mensagem }])
    enviar.mutate(
      { mensagem, historico },
      {
        onSuccess: (r) => {
          setHistorico(r.historico)
          setFalas((f) => [...f, { quem: 'assistente', texto: r.texto, acoes: r.acoes }])
        },
        onError: (e) => {
          // a fala sai da conversa e volta para a caixa: a pessoa reenvia sem redigitar
          setFalas((f) => f.slice(0, -1))
          setRascunho(mensagem)
          setErro(e instanceof Error ? e.message : 'O Orbind AI não conseguiu responder agora.')
        },
        onSettled: () => focarCaixa(),
      },
    )
  }

  const novaConversa = () => {
    setFalas([])
    setHistorico([])
    setErro(null)
    setRascunho('')
    focarCaixa()
  }

  const aoTeclar = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      mandar(rascunho)
    }
  }

  if (situacao.isPending) {
    return (
      <div className="ast ast--centro">
        <LoadingState variant="detail" label="Carregando o Orbind AI" />
      </div>
    )
  }
  if (situacao.isError) {
    return (
      <div className="ast ast--centro">
        <Alert tone="danger" title="Não foi possível abrir o Orbind AI">
          {situacao.error.message}
          <div className="ast__acoes-do-aviso">
            <Button size="sm" onClick={() => void situacao.refetch()}>
              Tentar de novo
            </Button>
          </div>
        </Alert>
      </div>
    )
  }

  const s = situacao.data
  if (!s.noPlano || !s.liberado || !s.configurado) {
    const [titulo, descricao] = !s.noPlano
      ? ['O Orbind AI não está no seu plano', 'Ele vem junto com os Conectores de IA. Fale com quem administra a conta.']
      : !s.liberado
        ? [
            'A IA ainda não foi liberada para você',
            'Quem administra o workspace libera na Administração, em Conectores de IA — lá também define o que a IA pode fazer por você.',
          ]
        : ['O Orbind AI ainda não foi configurado', 'O servidor do Orbind precisa da chave da Anthropic. Avise quem cuida da instalação.']
    return (
      <div className="ast ast--centro">
        <EmptyState icon="fas fa-lock" title={titulo} description={descricao} />
      </div>
    )
  }

  const soLeitura = s.permissoes.every((p) => p === 'consultar')

  return (
    <div className="ast">
      <header className="ast__topo">
        <h1 className="ast__titulo">
          <i className="fas fa-magic" aria-hidden />
          Orbind AI
        </h1>
        <span className="ast__permissoes">
          {soLeitura ? 'Somente consulta' : `Pode: ${s.permissoes.join(', ')}`}
        </span>
        <Button size="sm" icon="fas fa-plus" onClick={novaConversa} disabled={!falas.length || enviar.isPending}>
          Nova conversa
        </Button>
      </header>

      <div className="ast__conversa" aria-live="polite">
        {falas.length === 0 ? (
          <div className="ast__vazio">
            <EmptyState
              icon="fas fa-magic"
              title="Como posso ajudar?"
              description="Pergunte sobre os seus Itens de Trabalho, peça um resumo do dia ou para criar, comentar e mover Itens. Antes de mudar qualquer coisa eu confirmo com você."
            />
            <div className="ast__sugestoes">
              {SUGESTOES.map((t) => (
                <button
                  key={t}
                  type="button"
                  className="ast__sugestao"
                  onClick={() => (t.endsWith('…') ? (setRascunho(t.slice(0, -1)), focarCaixa()) : mandar(t))}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        ) : (
          falas.map((f, i) => (
            <div key={i} className={`ast__fala ast__fala--${f.quem}`}>
              <div className="ast__balao">
                {f.quem === 'assistente' ? <MarkdownDoAssistente texto={f.texto} /> : <p>{f.texto}</p>}
              </div>
              {f.acoes?.length ? (
                <div className="ast__feito">
                  <i className="fas fa-tools" aria-hidden />
                  {[...new Set(f.acoes.map((a) => ROTULO_DA_FERRAMENTA[a.ferramenta] ?? a.ferramenta))].join(' · ')}
                </div>
              ) : null}
            </div>
          ))
        )}
        {enviar.isPending ? (
          <div className="ast__fala ast__fala--assistente">
            <div className="ast__balao ast__balao--pensando" role="status">
              <span className="ast__pontos" aria-hidden>
                <i />
                <i />
                <i />
              </span>
              Pensando…
            </div>
          </div>
        ) : null}
        <div ref={fimRef} />
      </div>

      <footer className="ast__rodape">
        {erro ? (
          <Alert tone="danger" title="A resposta não veio">
            {erro}
          </Alert>
        ) : null}
        <div className="ast__caixa">
          <Textarea
            id={ID_DA_CAIXA}
            rows={2}
            value={rascunho}
            placeholder="Escreva para o Orbind AI — Enter envia, Shift+Enter quebra a linha"
            aria-label="Mensagem para o Orbind AI"
            onChange={(e) => setRascunho(e.target.value)}
            onKeyDown={aoTeclar}
            maxLength={8000}
          />
          <Button
            variant="primary"
            icon="fas fa-paper-plane"
            loading={enviar.isPending}
            disabled={!rascunho.trim()}
            onClick={() => mandar(rascunho)}
          >
            Enviar
          </Button>
        </div>
        <p className="ast__nota">
          As respostas são geradas por IA e podem errar. O que for feito fica no histórico do Item e na{' '}
          <button type="button" className="ast__link" onClick={() => navigate('/claude')}>
            sua lista de ações da IA
          </button>
          .
        </p>
      </footer>
    </div>
  )
}
